import { ProjectLanguage } from "@hex/common";
import React from "react";

import { HexToaster } from "../../common/Toasts";

export enum NotebookFileListSort {
  NAME = "Name",
  LAST_MODIFIED = "Last modified",
  SIZE = "Size",
}

export const PANDAS_FILE_EXTS = [
  "csv",
  "pkl",
  "xls",
  "xlsx",
  "json",
  "parquet",
];

export const R_FILE_EXTS = ["csv"];

export const DUCK_DB_FILE_EXTS = ["csv", "tsv", "parquet"];

export const sanitizeFilename = (filename: string): string => {
  // Extract the base name without the extension
  const baseName = filename.substring(0, filename.lastIndexOf(".")) || filename;

  // Normalize the string to remove accents/diacritics and then replace any characters that are not letters, digits, or underscores with an underscore
  const normalized = baseName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const cleaned = normalized.replace(/[^a-zA-Z0-9_]/g, "_");

  // Prepend "df_" if the base name starts with a digit.
  // (This used to just be an underscore, but that makes a hidden variable in Hex)
  return /^[0-9]/.test(cleaned) ? "df_" + cleaned : cleaned;
};

const snippetCreator = (
  filename: string,
  language: ProjectLanguage,
): string | null => {
  const extension = filename.split(".").pop();
  const python = language === ProjectLanguage.PYTHON;
  const sanitizedFilename = sanitizeFilename(filename);

  switch (extension) {
    case "csv":
      return python
        ? `${sanitizedFilename} = pd.read_csv("${filename}")`
        : `${sanitizedFilename} = read_csv("${filename}")`;
    case "pkl":
      return python
        ? `${sanitizedFilename} = pd.read_pkl("${filename}")`
        : null;
    case "xlsx":
      return python
        ? `${sanitizedFilename} = pd.read_excel("${filename}")`
        : null;
    case "xls":
      return python
        ? `${sanitizedFilename} = pd.read_excel("${filename}")`
        : null;
    case "json":
      return python
        ? `${sanitizedFilename} = pd.read_json("${filename}")`
        : null;
    case "parquet":
      return python
        ? `${sanitizedFilename} = pd.read_parquet("${filename}")`
        : null;
    default:
      return null;
  }
};

export const copyCodeSnippet = async ({
  filename,
  language,
  toaster,
}: {
  filename: string;
  language: ProjectLanguage;
  toaster: HexToaster;
}): Promise<void> => {
  const code =
    snippetCreator(filename, language) ??
    "Unable to copy dataframe creation code.";

  await navigator.clipboard.writeText(code);
  toaster.show({
    message: (
      <>
        <strong>Code snippet</strong> copied to clipboard.
      </>
    ),
    timeout: 2500,
  });
};

export const copyFilename = async ({
  filename,
  toaster,
}: {
  filename: string;
  toaster: HexToaster;
}): Promise<void> => {
  await navigator.clipboard.writeText(filename);
  toaster.show({
    message: (
      <>
        <strong>File name</strong> copied to clipboard.
      </>
    ),
    timeout: 2500,
  });
};
